import { useNavigate } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { RefreshCw } from "lucide-react";
import { ListPage } from "@/components/v2/list-page";
import { DataTable } from "@/components/v2/data-table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useDemoCatalog } from "@/hooks/use-demo-catalog";
import { RunFullPipelineButton } from "./_run-full-pipeline-button";

type Row = Record<string, unknown>;

const SIZE_LABELS: Record<string, string> = {
  small: "Small",
  medium: "Medium",
  large: "Large",
};

function formatNumber(n: unknown): string {
  if (typeof n !== "number") return "—";
  return n.toLocaleString();
}

function formatDate(iso: unknown): string {
  if (typeof iso !== "string") return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

const columns: ColumnDef<Row, unknown>[] = [
  {
    accessorKey: "display_name",
    header: "Dataset",
    cell: ({ row }) => (
      <span className="font-medium transition-colors group-hover:text-[color:var(--color-accent)]">
        {(row.original.display_name as string) || "—"}
      </span>
    ),
  },
  {
    accessorKey: "business",
    header: "Business",
    cell: ({ row }) => (
      <span className="text-[color:var(--color-text-lo)]">
        {(row.original.business as string) || "—"}
      </span>
    ),
  },
  {
    accessorKey: "industry",
    header: "Industry",
    enableSorting: false,
    cell: ({ row }) => (
      <span className="text-[color:var(--color-text-lo)]">
        {(row.original.industry as string) || "—"}
      </span>
    ),
  },
  {
    accessorKey: "size_label",
    header: "Size",
    enableSorting: false,
    cell: ({ row }) => {
      const size = row.original.size_label as string | undefined;
      return (
        <Badge variant="secondary">
          {SIZE_LABELS[size ?? ""] ?? size ?? "—"}
        </Badge>
      );
    },
  },
  {
    accessorKey: "total_records",
    header: "Records",
    meta: { numeric: true },
    cell: ({ row }) => (
      <span className="typo-mono">
        {formatNumber(row.original.total_records)}
      </span>
    ),
  },
  {
    accessorKey: "entity_types",
    header: "Entity types",
    meta: { numeric: true },
    cell: ({ row }) => (
      <span className="typo-mono">
        {formatNumber(row.original.entity_types)}
      </span>
    ),
  },
  {
    accessorKey: "tier_count",
    header: "Tiers",
    meta: { numeric: true },
    cell: ({ row }) => (
      <span className="typo-mono">{formatNumber(row.original.tier_count)}</span>
    ),
  },
  {
    accessorKey: "generated_at",
    header: "Generated",
    cell: ({ row }) => (
      <span className="typo-mono text-[color:var(--color-text-lo)]">
        {formatDate(row.original.generated_at)}
      </span>
    ),
  },
];

export default function V2SettingsDemoCatalogPage() {
  const navigate = useNavigate();
  const { data: catalog, isLoading, isError, refetch } = useDemoCatalog();

  const rows: Row[] = catalog?.datasets ?? [];

  return (
    <ListPage
      title="Demo Catalog"
      breadcrumbs={[{ label: "Settings" }, { label: "Demo Catalog" }]}
      actions={
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
            Refresh
          </Button>
          <RunFullPipelineButton />
        </div>
      }
    >
      <DataTable
        columns={columns}
        data={rows}
        total={rows.length}
        isLoading={isLoading}
        isError={isError}
        errorEntityLabel="demo datasets"
        onRetry={() => void refetch()}
        onRowClick={(row) =>
          navigate(`/settings/demo-catalog/${row.id as string}`)
        }
        emptyMessage="No demo datasets"
        emptyDescription="The demo catalog is empty. Generate datasets via the demo-data generator."
      />
    </ListPage>
  );
}
